// Choosing Kitchen or Counter for this device, and the PIN to leave (docs/plans/stations.md).
// Boards: StationPick, StationPinSet, StationPinEnter, StationPinLocked.
import { Button, Field, Modal, PasswordInput, cx, useToast } from '@ims/ui'
import { ChefHat, Monitor, Store } from '@ims/ui/icons'
import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { errorMessage, useOnline } from '../hooks.ts'
import { PIN_LENGTH, checkPin, clearPin, hasPin, pinWait, savePin } from '../lib/station-pin.ts'
import { setStation, stationHome, type StationMode } from '../lib/station.ts'
import { WrongPasswordError, useSession, useUser } from '../session.tsx'
import { useWorkspace } from '../workspace.tsx'

const MODE_WORDS: Record<'kitchen' | 'counter', string> = { kitchen: 'Kitchen', counter: 'Counter' }

/** A number pad big enough for wet or floury fingers; the keyboard's digits work too. */
function PinPad({
  length,
  disabled,
  onChange,
  onFull,
}: {
  length: number
  disabled?: boolean
  onChange: (next: number) => void
  onFull: (digit: string) => void
}) {
  const key = (label: string, onClick: () => void, plain = false) => (
    <button
      key={label}
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cx(
        'h-[4.5rem] rounded-card font-bold transition-transform duration-150 ease-out enabled:active:scale-[0.97] disabled:opacity-40',
        plain
          ? 'text-base font-semibold text-muted hover:bg-soft'
          : 'tabular border border-border bg-surface text-3xl hover:bg-soft',
      )}
    >
      {label}
    </button>
  )
  return (
    <div className="grid grid-cols-3 gap-2.5">
      {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => key(d, () => onFull(d)))}
      {key('Clear', () => onChange(0), true)}
      {key('0', () => onFull('0'))}
      {key('Delete', () => onChange(Math.max(0, length - 1)), true)}
    </div>
  )
}

function PinDots({ length, bad }: { length: number; bad?: boolean }) {
  return (
    <div
      role="img"
      aria-label={`${length} of ${PIN_LENGTH} digits`}
      className="flex justify-center gap-4 py-2"
    >
      {Array.from({ length: PIN_LENGTH }, (_, i) => (
        <span
          key={i}
          className={cx(
            'size-[18px] rounded-full',
            i < length ? (bad ? 'bg-danger' : 'bg-primary') : 'border-2 border-border',
          )}
        />
      ))}
    </div>
  )
}

/** Digits typed so far, from the pad or the keyboard, reported once there are four. */
function usePinEntry(onDone: (pin: string) => void, disabled = false) {
  const [pin, setPin] = useState('')
  const add = (digit: string) => {
    if (disabled) return
    const next = (pin + digit).slice(0, PIN_LENGTH)
    setPin(next)
    if (next.length === PIN_LENGTH) {
      setPin('')
      onDone(next)
    }
  }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return
      if (/^[0-9]$/.test(e.key)) add(e.key)
      else if (e.key === 'Backspace') setPin((p) => p.slice(0, -1))
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  })
  return {
    length: pin.length,
    add,
    setLength: (n: number) => setPin((p) => p.slice(0, n)),
  }
}

function PinSet({ mode, onDone }: { mode: 'kitchen' | 'counter'; onDone: () => void }) {
  const user = useUser()
  const { current } = useWorkspace()
  const [first, setFirst] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const entry = usePinEntry((pin) => {
    if (first === null) {
      setFirst(pin)
      setMessage(null)
    } else if (pin === first) {
      if (current) void savePin(user.id, current.tenant_id, pin).then(onDone)
    } else {
      setFirst(null)
      setMessage('Those didn’t match. Choose a PIN again.')
    }
  })
  return (
    <div className="space-y-2">
      <PinDots length={entry.length} />
      <p
        className={cx(
          'min-h-5 text-center text-sm font-semibold',
          message ? 'text-danger' : 'text-muted',
        )}
      >
        {message ?? (first === null ? 'Choose 4 digits' : 'Type it again to confirm')}
      </p>
      <PinPad length={entry.length} onChange={entry.setLength} onFull={entry.add} />
      <p className="pt-1 text-sm text-muted">
        To stop staff leaving the app too, turn on screen pinning (Android) or Guided Access (iPad).
        You’ll need this PIN to leave {MODE_WORDS[mode]}.
      </p>
    </div>
  )
}

/** "Use this device as": from the account menu. */
export function StationPickDialog({ onClose }: { onClose: () => void }) {
  const user = useUser()
  const { current } = useWorkspace()
  const { platform } = useSession()
  const navigate = useNavigate()
  const [mode, setMode] = useState<'full' | 'kitchen' | 'counter'>('counter')
  const [choosingPin, setChoosingPin] = useState(false)
  const tenantId = current?.tenant_id
  const go = () => {
    if (!tenantId || mode === 'full') return onClose()
    // Asked for here, while a tap has just happened; the browser refuses it otherwise.
    platform.station?.enterFullscreen()
    setStation(user.id, tenantId, mode)
    navigate(stationHome(mode), { replace: true })
    onClose()
  }
  const start = () => {
    if (!tenantId) return
    if (mode !== 'full' && !hasPin(user.id, tenantId)) setChoosingPin(true)
    else go()
  }
  if (choosingPin && mode !== 'full')
    return (
      <Modal
        open
        onClose={() => setChoosingPin(false)}
        size="sm"
        title="Choose a PIN for this device"
        subtitle={`You’ll need it to leave ${MODE_WORDS[mode]}. It works on this device only.`}
        footer={
          <Button variant="ghost" onClick={go}>
            Skip, no PIN
          </Button>
        }
      >
        <PinSet mode={mode} onDone={go} />
      </Modal>
    )
  const option = (value: typeof mode, icon: ReactNode, title: string, sub: string) => (
    <label
      className={cx(
        'flex cursor-pointer items-center gap-3 rounded-card border bg-surface p-3.5',
        mode === value ? 'border-primary ring-1 ring-primary' : 'border-border hover:bg-soft',
      )}
    >
      <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-soft text-muted">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold">{title}</span>
        <span className="block text-sm text-muted">{sub}</span>
      </span>
      <input
        type="radio"
        name="station-mode"
        className="size-5 accent-primary"
        checked={mode === value}
        onChange={() => setMode(value)}
      />
    </label>
  )
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title="Use this device as"
      subtitle="Only this device changes. Everyone else keeps their own."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={start}>
            {mode === 'full' ? 'Keep the full app' : `Use ${MODE_WORDS[mode]}`}
          </Button>
        </>
      }
    >
      <div className="space-y-2.5">
        {option(
          'full',
          <Monitor className="size-5" aria-hidden />,
          'Full app',
          'Everything your role allows',
        )}
        {option(
          'counter',
          <Store className="size-5" aria-hidden />,
          'Counter',
          'Tables, menu and checkout, full screen',
        )}
        {option(
          'kitchen',
          <ChefHat className="size-5" aria-hidden />,
          'Kitchen',
          'Tickets only, full screen, stays awake',
        )}
        {tenantId && hasPin(user.id, tenantId) && (
          <button
            type="button"
            className="text-sm font-semibold text-primary hover:underline"
            onClick={() => {
              clearPin(user.id, tenantId)
              setMode((m) => (m === 'full' ? 'counter' : m))
              setChoosingPin(true)
            }}
          >
            Change the PIN
          </button>
        )}
      </div>
    </Modal>
  )
}

/** The way out of a mode: the PIN if the device has one, or the account's password. */
export function StationLeaveDialog({
  mode,
  onClose,
}: {
  mode: Exclude<StationMode, 'full'>
  onClose: () => void
}) {
  const user = useUser()
  const session = useSession()
  const { current } = useWorkspace()
  const online = useOnline()
  const toast = useToast()
  const navigate = useNavigate()
  const tenantId = current?.tenant_id ?? ''
  const [wrong, setWrong] = useState(false)
  const [usePassword, setUsePassword] = useState(false)
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [wait, setWait] = useState(() => pinWait(user.id, tenantId))
  const pinned = hasPin(user.id, tenantId)
  const leave = () => {
    setStation(user.id, tenantId, 'full')
    onClose()
    navigate('/', { replace: true })
  }
  useEffect(() => {
    if (wait <= 0) return
    const id = setInterval(() => setWait(pinWait(user.id, tenantId)), 1000)
    return () => clearInterval(id)
  }, [wait, user.id, tenantId])
  const entry = usePinEntry(
    (pin) =>
      void checkPin(user.id, tenantId, pin).then((ok) => {
        if (ok) return leave()
        setWrong(true)
        setWait(pinWait(user.id, tenantId))
      }),
    wait > 0 || usePassword,
  )
  const tryPassword = async () => {
    setBusy(true)
    try {
      await session.verifyPassword(password)
      clearPin(user.id, tenantId)
      leave()
    } catch (error) {
      toast.error(error instanceof WrongPasswordError ? error.message : errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  const title = `Leave ${MODE_WORDS[mode]}?`
  const subtitle = 'This device goes back to the full app.'
  if (!pinned)
    return (
      <Modal
        open
        onClose={onClose}
        size="sm"
        title={title}
        footer={
          <>
            <Button variant="secondary" onClick={leave}>
              Leave
            </Button>
            <Button onClick={onClose}>Stay in {MODE_WORDS[mode]}</Button>
          </>
        }
      >
        <p className="text-sm text-muted">{subtitle} New work won’t show here until you return.</p>
      </Modal>
    )
  if (usePassword)
    return (
      <Modal
        open
        onClose={onClose}
        size="sm"
        title={title}
        subtitle={subtitle}
        footer={
          <>
            <Button variant="secondary" onClick={() => setUsePassword(false)}>
              Back to PIN
            </Button>
            <Button
              loading={busy}
              disabled={!online || !password}
              onClick={() => void tryPassword()}
            >
              Leave
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <Field label="Password">
            {(id) => (
              <PasswordInput
                id={id}
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>
          <p className="text-sm text-muted">
            {online
              ? `The password of ${user.email ?? 'this account'}. This also removes the PIN.`
              : 'Checking a password needs the internet. Connect, then try again.'}
          </p>
        </div>
      </Modal>
    )
  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      title={title}
      subtitle={subtitle}
      footer={
        <Button variant="ghost" onClick={() => setUsePassword(true)}>
          Use password instead
        </Button>
      }
    >
      <div className="space-y-2">
        <PinDots length={entry.length} bad={wrong && entry.length === 0} />
        <p
          role="status"
          className={cx(
            'min-h-5 text-center text-sm font-semibold',
            wait > 0 || wrong ? 'text-danger' : 'text-muted',
          )}
        >
          {wait > 0
            ? `Wrong PIN 5 times. Try again in ${Math.ceil(wait / 1000)} seconds.`
            : wrong
              ? 'Wrong PIN. Try again.'
              : 'Enter the PIN'}
        </p>
        <PinPad
          length={entry.length}
          disabled={wait > 0}
          onChange={entry.setLength}
          onFull={entry.add}
        />
      </div>
    </Modal>
  )
}
